import { IMilkingProcessDeviationEntry } from 'models/MilkingProcess'
import { useEffect, useState } from 'react'

enum NavigationDirection {
	Next = 'next',
	Prev = 'prev',
}

type UseDeviationVideoModalParams = {
	currentPage: number
	totalCount: number
	pageSize: number
	onPageChange: (page: number) => void
	currentPageDeviations: IMilkingProcessDeviationEntry[]
}

export const useDeviationVideoModal = ({
	currentPage,
	totalCount,
	pageSize,
	onPageChange,
	currentPageDeviations,
}: UseDeviationVideoModalParams) => {
	const [isVideoOpen, setIsVideoOpen] = useState(false)
	const [videoUrl, setVideoUrl] = useState<string | null>(null)
	const [selectedEntry, setSelectedEntry] =
		useState<IMilkingProcessDeviationEntry | null>(null)
	const [currentIndex, setCurrentIndex] = useState<number | null>(null)
	const [pendingNavigation, setPendingNavigation] =
		useState<NavigationDirection | null>(null)

	const openModal = (
		entry: IMilkingProcessDeviationEntry,
		allDeviations: IMilkingProcessDeviationEntry[],
	) => {
		setSelectedEntry(entry)
		setVideoUrl(entry.video_url)
		setIsVideoOpen(true)
		setCurrentIndex(allDeviations.findIndex(d => d.id === entry.id))
	}

	const closeModal = () => {
		setIsVideoOpen(false)
		setVideoUrl(null)
		setSelectedEntry(null)
		setCurrentIndex(null)
		setPendingNavigation(null)
	}

	const totalPages = Math.ceil(totalCount / pageSize)
	const hasPrevPage = currentPage > 1
	const hasNextPage = currentPage < totalPages

	const canPrev = currentIndex !== null && (currentIndex > 0 || hasPrevPage)
	const canNext =
		currentIndex !== null &&
		(currentIndex < currentPageDeviations.length - 1 || hasNextPage)

	const showNext = () => {
		if (currentIndex !== null) {
			if (currentIndex < currentPageDeviations.length - 1) {
				// Переход к следующему отклонению на текущей странице
				const nextIndex = currentIndex + 1
				setCurrentIndex(nextIndex)
				const nextEntry = currentPageDeviations[nextIndex]
				setSelectedEntry(nextEntry)
				setVideoUrl(nextEntry.video_url)
			} else if (hasNextPage) {
				// Переход на следующую страницу
				setPendingNavigation(NavigationDirection.Next)
				onPageChange(currentPage + 1)
			}
		}
	}

	const showPrev = () => {
		if (currentIndex !== null) {
			if (currentIndex > 0) {
				// Переход к предыдущему отклонению на текущей странице
				const prevIndex = currentIndex - 1
				setCurrentIndex(prevIndex)
				const prevEntry = currentPageDeviations[prevIndex]
				setSelectedEntry(prevEntry)
				setVideoUrl(prevEntry.video_url)
			} else if (hasPrevPage) {
				// Переход на предыдущую страницу
				setPendingNavigation(NavigationDirection.Prev)
				onPageChange(currentPage - 1)
			}
		}
	}

	// Обновление данных при изменении страницы (синхронизация с текущей страницей)
	useEffect(() => {
		if (isVideoOpen && pendingNavigation && currentPageDeviations.length > 0) {
			if (pendingNavigation === NavigationDirection.Next) {
				// Переключаемся на первое отклонение новой страницы
				const firstEntry = currentPageDeviations[0]
				setCurrentIndex(0)
				setSelectedEntry(firstEntry)
				setVideoUrl(firstEntry.video_url)
			} else if (pendingNavigation === NavigationDirection.Prev) {
				// Переключаемся на последнее отклонение новой страницы
				const lastIndex = currentPageDeviations.length - 1
				const lastEntry = currentPageDeviations[lastIndex]
				setCurrentIndex(lastIndex)
				setSelectedEntry(lastEntry)
				setVideoUrl(lastEntry.video_url)
			}
			setPendingNavigation(null)
		}
	}, [currentPageDeviations, pendingNavigation, isVideoOpen])

	useEffect(() => {
		if (!isVideoOpen || !selectedEntry) {
			return
		}

		const updatedEntry = currentPageDeviations.find(
			deviation => deviation.id === selectedEntry.id,
		)

		if (updatedEntry) {
			setSelectedEntry(updatedEntry)
			setVideoUrl(updatedEntry.video_url)
		}
	}, [currentPageDeviations, isVideoOpen, selectedEntry])

	return {
		isVideoOpen,
		videoUrl,
		selectedEntry,
		openModal,
		closeModal,
		canPrev,
		canNext,
		showPrev,
		showNext,
	}
}
